// Client-side mirror of the server's ProbeScorer (Application/Ingestion/ProbeScorer.cs).
//
// The engine measures; the server scores authoritatively at ingestion. For the *live display*
// (the home auto-run, the vanity check page, the admin diagnostics harness) we still need an
// advisory verdict before anything is posted — and it must match what the server will store, or
// a shared link would show different pills than the page that created it. So both sides run the
// SAME declarative ruleset over the SAME effective thresholds (delivered per probe in the engine
// config). Keep this in lock-step with ProbeScorer.

import type { ProbeOutcome, ProbeValue, Thresholds } from './types';

/** Lowercase result type, mirroring the server catalog's ProbeResultType. */
export type ProbeResultType = 'boolean' | 'numeric' | 'categorical';

function hasConstraints(t: Thresholds | undefined): t is Thresholds {
  return (
    !!t &&
    (t.expected !== undefined ||
      t.min !== undefined ||
      t.max !== undefined ||
      (t.allowed?.length ?? 0) > 0 ||
      (t.containsAny?.length ?? 0) > 0)
  );
}

function scoreBoolean(value: ProbeValue, t: Thresholds, mismatch: ProbeOutcome): ProbeOutcome {
  if (t.expected === undefined) {
    return 'pass';
  }
  return value === t.expected ? 'pass' : mismatch;
}

function scoreNumeric(value: ProbeValue, t: Thresholds, mismatch: ProbeOutcome): ProbeOutcome {
  if (typeof value !== 'number') {
    return mismatch;
  }
  const withinMin = t.min === undefined || value >= t.min;
  const withinMax = t.max === undefined || value <= t.max;
  return withinMin && withinMax ? 'pass' : mismatch;
}

function scoreCategorical(value: ProbeValue, t: Thresholds, mismatch: ProbeOutcome): ProbeOutcome {
  if (typeof value !== 'string') {
    return (t.allowed?.length ?? 0) > 0 || (t.containsAny?.length ?? 0) > 0 ? mismatch : 'pass';
  }
  const text = value.toLowerCase();
  if (t.allowed && t.allowed.length > 0) {
    return t.allowed.some((a) => a.toLowerCase() === text) ? 'pass' : mismatch;
  }
  // Substring match (any): the value is often a joined list, e.g. "H.264, VP9".
  if (t.containsAny && t.containsAny.length > 0) {
    return t.containsAny.some((token) => text.includes(token.toLowerCase())) ? 'pass' : mismatch;
  }
  return 'pass';
}

/**
 * Scores one measured value against its (effective) thresholds, identically to the server:
 * - no constraints → informational Pass
 * - blocked → Pass when blockedIsPass or unconstrained, else Warn (couldn't verify)
 * - otherwise compare by result type; a mismatch is Fail, or Warn when onMismatch:"warn"
 */
export function scoreByThresholds(
  value: ProbeValue,
  blocked: boolean,
  resultType: ProbeResultType,
  thresholds: Thresholds | undefined,
): ProbeOutcome {
  if (blocked) {
    if ((thresholds && thresholds.blockedIsPass === true) || !hasConstraints(thresholds)) {
      return 'pass';
    }
    return 'warn';
  }
  if (!hasConstraints(thresholds)) {
    return 'pass';
  }
  const mismatch: ProbeOutcome = thresholds.onMismatch === 'warn' ? 'warn' : 'fail';
  switch (resultType) {
    case 'boolean':
      return scoreBoolean(value, thresholds, mismatch);
    case 'numeric':
      return scoreNumeric(value, thresholds, mismatch);
    case 'categorical':
      return scoreCategorical(value, thresholds, mismatch);
    default:
      return 'pass';
  }
}
